import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../components/layout/Layout';
import api from '../services/api';
import { fmtDateTime } from '../utils/helpers';
import {useTranslation} from 'react-i18next';

const STATUS_CSS: Record<string, string> = {
  ok: 'bg-green-100 text-green-700', up: 'bg-green-100 text-green-700', configured: 'bg-green-100 text-green-700',
  degraded: 'bg-amber-100 text-amber-700', demo_mode: 'bg-amber-100 text-amber-700', env_configured_not_integrated: 'bg-amber-100 text-amber-700',
  down: 'bg-red-100 text-red-700', not_configured: 'bg-slate-100 text-slate-500', unavailable: 'bg-slate-100 text-slate-500',
};
const STATUS_LABEL: Record<string, string> = {
  ok: 'OK', up: 'Up', configured: 'Configured', degraded: 'Degraded', demo_mode: 'Demo mode',
  env_configured_not_integrated: 'Configured, not integrated', down: 'Down', not_configured: 'Not configured', unavailable: 'Unavailable',
};

function Pill({ status }: { status: string }) {
  return <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_CSS[status] || 'bg-slate-100 text-slate-500'}`}>{STATUS_LABEL[status] || status}</span>;
}

function Card({ title, children, status }: { title: string; children: React.ReactNode; status?: string }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-gray-700">{title}</h3>
        {status && <Pill status={status} />}
      </div>
      {children}
    </div>
  );
}

function Bar({ percent }: { percent: number }) {
  const color = percent >= 90 ? '#dc2626' : percent >= 75 ? '#d97706' : '#039089';
  return (
    <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden">
      <div className="h-full rounded-full" style={{ width: `${Math.min(percent, 100)}%`, background: color }} />
    </div>
  );
}

function fmtUptime(sec: number) {
  const d = Math.floor(sec / 86400), h = Math.floor((sec % 86400) / 3600), m = Math.floor((sec % 3600) / 60);
  return `${d}d ${h}h ${m}m`;
}

export default function SystemHealthPage() {
  const {t}=useTranslation();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    api.getSystemHealthDetailed()
      .then(r => { setData(r.data); setLastChecked(new Date()); })
      .catch(e => setError(e.response?.data?.message || 'Failed to load system health. You may not have permission to view this page.'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <Layout title="System Health & Monitoring" actions={
      <button onClick={load} disabled={loading} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:border-gray-300 disabled:opacity-50">
        {loading ? 'Refreshing…' : '↻ Refresh'}
      </button>
    }>
      <div className="max-w-5xl mx-auto">
        {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">{error}</div>}

        {data && (
          <>
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <Pill status={data.status} />
                <span className="text-sm text-gray-500">Uptime: {fmtUptime(data.uptimeSeconds)}</span>
              </div>
              {lastChecked && <span className="text-xs text-gray-400">Last checked {fmtDateTime(lastChecked.toISOString())}</span>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <Card title="Database" status={data.database?.status}>
                <p className="text-sm text-gray-600">{data.database?.status === 'up' ? `Responding in ${data.database.latencyMs}ms` : data.database?.error || 'Unreachable'}</p>
              </Card>
              <Card title="Disk Usage" status={data.disk?.status === 'ok' ? undefined : data.disk?.status}>
                {data.disk?.status === 'ok' ? (
                  <>
                    <Bar percent={data.disk.usedPercent} />
                    <p className="text-xs text-gray-500 mt-2">{data.disk.usedMB.toLocaleString()} MB used of {data.disk.totalMB.toLocaleString()} MB ({data.disk.usedPercent}%)</p>
                  </>
                ) : <p className="text-sm text-gray-400">Disk stats unavailable on this platform.</p>}
              </Card>
              <Card title="Memory">
                <Bar percent={data.memory.usedPercent} />
                <p className="text-xs text-gray-500 mt-2">{data.memory.usedMB.toLocaleString()} MB used of {data.memory.totalMB.toLocaleString()} MB ({data.memory.usedPercent}%)</p>
              </Card>
              <Card title="CPU Load">
                <p className="text-sm text-gray-600">{data.cpu.cores} cores</p>
                <p className="text-xs text-gray-500 mt-1">Load avg: {data.cpu.loadAvg1m} (1m) · {data.cpu.loadAvg5m} (5m) · {data.cpu.loadAvg15m} (15m)</p>
              </Card>
              <Card title="Email (SMTP)" status={data.email?.status}>
                <p className="text-sm text-gray-600">{data.email?.host || 'No SMTP host configured'}</p>
              </Card>
              <Card title="SMS Gateway" status={data.sms?.status}>
                <p className="text-sm text-gray-600">{data.sms?.note || 'Gateway key configured'}</p>
              </Card>
              <Card title="Redis" status={data.redis?.status}>
                <p className="text-sm text-gray-600">{data.redis?.note}</p>
              </Card>
              <Card title="Backup" status={data.backup?.status}>
                <p className="text-sm text-gray-600">{data.backup?.note}</p>
              </Card>
              <Card title="SSL / Certificate Monitoring" status={data.ssl?.status}>
                <p className="text-sm text-gray-600">{data.ssl?.note}</p>
              </Card>
            </div>

            <Card title="Scheduled Jobs">
              <div className="divide-y divide-gray-100">
                {(data.scheduledJobs || []).map((j: any, i: number) => (
                  <div key={i} className="flex items-center justify-between py-2 text-sm">
                    <span className="text-gray-700">{j.name}</span>
                    <span className="text-gray-400 text-xs">{j.schedule}</span>
                  </div>
                ))}
              </div>
            </Card>
          </>
        )}

        {loading && !data && <div className="text-center py-16 text-gray-400 text-sm">{t('common.loading','Loading…')}</div>}
      </div>
    </Layout>
  );
}
